import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudSyncRounded,
  DeleteOutlineRounded,
  SaveRounded,
  VideocamRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmProvider';
import { useTenantStore } from '../../stores/tenant.store';
import { zoomApi, type ZoomIntegrationStateDto } from '../../lib/api/zoom.api';
import { errorText } from '../../lib/error-text';
import { formatDateTime } from '../../lib/i18n-format';

export function ZoomIntegrationPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const confirm = useConfirm();
  const { currentTenantId } = useTenantStore();
  const qc = useQueryClient();

  const stateQ = useQuery({
    queryKey: ['zoom-integration-state', currentTenantId],
    enabled: !!currentTenantId,
    queryFn: () => zoomApi.getState({ tenantId: currentTenantId! }),
  });

  const [accountId, setAccountId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (stateQ.data) {
      setAccountId(stateQ.data.accountId ?? '');
      setClientId(stateQ.data.clientId ?? '');
      // Never echo the secret back from the server. Field stays empty unless
      // the admin types a new one.
      setClientSecret('');
    }
  }, [stateQ.data]);

  const save = async () => {
    if (!currentTenantId) return;
    if (!accountId || !clientId || !clientSecret) {
      setError('Account ID, Client ID y Client Secret son obligatorios');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await zoomApi.configure({ tenantId: currentTenantId, accountId, clientId, clientSecret });
      setInfo('Credenciales guardadas');
      setClientSecret('');
      qc.invalidateQueries({ queryKey: ['zoom-integration-state'] });
    } catch (e) {
      setError(errorText(e, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const syncNow = async () => {
    if (!currentTenantId) return;
    setSyncing(true);
    setError(null);
    setInfo(null);
    try {
      const res = await zoomApi.syncNow({ tenantId: currentTenantId });
      setInfo(
        `Sincronización completada: ${res.meetingsFound} reuniones encontradas, ${res.materialsCreated} grabaciones importadas.`,
      );
      qc.invalidateQueries({ queryKey: ['zoom-integration-state'] });
    } catch (e) {
      setError(errorText(e, 'Error al sincronizar'));
    } finally {
      setSyncing(false);
    }
  };

  const revoke = async () => {
    if (!currentTenantId) return;
    if (!(await confirm({ message: t('zoom.revoke.confirm'), confirmColor: 'error' }))) return;
    setRevoking(true);
    setError(null);
    try {
      await zoomApi.revoke({ tenantId: currentTenantId });
      setInfo(t('zoom.revoke.success'));
      qc.invalidateQueries({ queryKey: ['zoom-integration-state'] });
    } catch (e) {
      setError(errorText(e, t('zoom.revoke.error')));
    } finally {
      setRevoking(false);
    }
  };

  const state = stateQ.data;

  return (
    <Box sx={{ maxWidth: 820, p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <VideocamRounded sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t('zoom.page.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('zoom.page.subtitle')}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Card sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Credenciales Server-to-Server OAuth
          </Typography>
          <ZoomStatusChip state={state} loading={stateQ.isLoading} />
        </Stack>

        <Stack spacing={2}>
          <TextField
            label="Account ID"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            fullWidth
            size="small"
            helperText="Encuéntralo en Zoom Marketplace → Build App → S2S OAuth → Account ID"
          />
          <TextField
            label="Client ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Client Secret"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            type={showSecret ? 'text' : 'password'}
            fullWidth
            size="small"
            placeholder={state?.configured ? '••••••••• (deja vacío para conservar)' : ''}
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={() => setShowSecret((v) => !v)}
                  edge="end"
                  aria-label="Mostrar/ocultar secret"
                >
                  {showSecret ? (
                    <VisibilityOffRounded fontSize="small" />
                  ) : (
                    <VisibilityRounded fontSize="small" />
                  )}
                </IconButton>
              ),
            }}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveRounded />}
            onClick={save}
            disabled={saving}
          >
            {state?.configured ? 'Actualizar credenciales' : 'Guardar credenciales'}
          </Button>
          <Tooltip title="Trae grabaciones nuevas desde Zoom y las importa como material de curso">
            <span>
              <Button
                variant="outlined"
                startIcon={
                  syncing ? <CircularProgress size={14} color="inherit" /> : <CloudSyncRounded />
                }
                onClick={syncNow}
                disabled={syncing || !state?.configured}
              >
                Sincronizar ahora
              </Button>
            </span>
          </Tooltip>
          {state?.configured && (
            <Button
              variant="outlined"
              color="error"
              startIcon={
                revoking ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineRounded />
              }
              onClick={revoke}
              disabled={revoking}
            >
              Revocar
            </Button>
          )}
        </Stack>
      </Card>

      <Card sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Cómo funciona la sincronización
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <ol style={{ paddingLeft: 20, marginTop: 8 }}>
            <li>
              El sistema corre todos los días a las 04:00 (Chile) y consulta a Zoom las grabaciones
              nuevas desde la última sincronización exitosa.
            </li>
            <li>
              Para cada grabación encontrada, busca el curso asociado al usuario Zoom anfitrión y
              crea un material tipo <strong>video</strong> en cada sección activa del curso.
            </li>
            <li>
              Las grabaciones quedan disponibles en el portal del alumno bajo el tab Material del
              curso, con el enlace play directo de Zoom.
            </li>
            <li>Se omiten grabaciones ya importadas (idempotente por URL).</li>
          </ol>
          También puedes presionar <strong>Sincronizar ahora</strong> para correrlo manualmente, y
          luego revisar el estado abajo.
        </Typography>
        {state && (
          <Stack direction="row" spacing={2} mt={2} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Última sync: ${state.lastSyncAt ? formatDateTime(state.lastSyncAt) : 'nunca'}`}
            />
            <Chip
              size="small"
              color={
                state.status === 'active'
                  ? 'success'
                  : state.status === 'error'
                    ? 'error'
                    : 'default'
              }
              label={`Estado: ${state.status}`}
            />
            <Chip
              size="small"
              label={`Cursos mapeados: ${state.courseHostMap ? Object.keys(state.courseHostMap).length : 0}`}
            />
          </Stack>
        )}
        {state?.lastSyncError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Último error: {state.lastSyncError}
          </Alert>
        )}
      </Card>
    </Box>
  );
}

function ZoomStatusChip({
  state,
  loading,
}: {
  state?: ZoomIntegrationStateDto | undefined;
  loading?: boolean | undefined;
}): JSX.Element {
  if (loading) return <CircularProgress size={16} />;
  if (!state || !state.configured) {
    return <Chip size="small" label="Sin configurar" variant="outlined" />;
  }
  if (state.status === 'error') {
    return <Chip size="small" label="Error" color="error" />;
  }
  return <Chip size="small" label="Activa" color="success" />;
}
